import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'src/components/PhoneInput';
import theme from 'src/theme';
import useStyles from '../styles';
import makeInitialState from './initialState';
import validationSchema from './schema';

const StepPersonal = ({ data, onNext, onBack }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const initialState = useMemo(() => makeInitialState(data), [data]);

  const handleSubmit = useCallback(values => onNext(values), [onNext]);

  return (
    <Formik
      initialValues={initialState}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        errors,
        handleSubmit: formikSubmit,
        touched,
        values,
        handleBlur,
        handleChange,
        setFieldValue
      }) => (
        <form onSubmit={formikSubmit}>
          <Box>
            <Typography
              variant="h3"
              sx={{ marginBottom: '16px', color: theme.palette.primary.main }}
            >
              {t('AVAL_BURO_FORM.STEP_PERSONAL.TITLE')}
            </Typography>

            <TextField
              error={Boolean(touched.name && errors.name)}
              fullWidth
              helperText={touched.name && t(errors.name)}
              label={t('COMMON.NAME')}
              margin="normal"
              name="name"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.name}
              variant="outlined"
            />

            <Grid container spacing={{ xs: 0, sm: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  error={Boolean(touched.paternalName && errors.paternalName)}
                  fullWidth
                  helperText={touched.paternalName && t(errors.paternalName)}
                  label={t('COMMON.PATERNAL_NAME')}
                  margin="normal"
                  name="paternalName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.paternalName}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('COMMON.MATERNAL_NAME')}
                  margin="normal"
                  name="maternalName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.maternalName}
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <FormControl fullWidth margin="normal">
              <PhoneInput
                id="phone-label"
                name="phone"
                label={t('COMMON.PHONE')}
                value={values.phone}
                onChange={setFieldValue}
                onBlur={handleBlur}
                error={Boolean(touched.phone && errors.phone)}
              />
              {Boolean(touched.phone && errors.phone) && (
                <FormHelperText sx={{ color: '#d32f2f' }}>
                  {t(errors.phone)}
                </FormHelperText>
              )}
            </FormControl>

            <TextField
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && t(errors.email)}
              label={t('COMMON.EMAIL')}
              margin="normal"
              name="email"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.email}
              variant="outlined"
              type="email"
              inputProps={{ inputMode: 'email', autoCapitalize: 'none' }}
            />

            <TextField
              fullWidth
              label={t('AVAL_BURO_FORM.STEP_PERSONAL.KINSHIP')}
              margin="normal"
              name="kinship"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.kinship}
              variant="outlined"
            />
          </Box>

          <Box className={classes.box}>
            <Button
              className={classes.buttons}
              onClick={onBack}
              color="primary"
              variant="outlined"
            >
              {t('COMMON.BACK')}
            </Button>
            <Button
              className={clsx(classes.buttons, classes.buttonMain)}
              type="submit"
              color="primary"
              variant="contained"
            >
              {t('COMMON.NEXT')}
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  );
};

StepPersonal.propTypes = {
  data: PropTypes.object,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

export default StepPersonal;
